import React, { useEffect, useState } from "react";
import Dialog from "../base/ui/components/web/Dialog";
import Container from "../base/react/components/web/Container";
import { useDispatch } from "react-redux";
import { showErrorNotification, showSuccessNotification } from "../notifications/actions";
import { NOTIFICATION_TIMEOUT_TYPE } from "../notifications/constants";
import { addElementsToExcalidraw, createImageElement } from "../whiteboard/utils";
import { hideDialog, openDialog } from "../base/dialog/actions";
import { RemoveImageDialog } from "../collab-images/RemoveImageDialog";
import Button from "../base/ui/components/web/Button";
import { BUTTON_TYPES } from "../base/ui/constants.any";
import { IconArrowBack, IconOdontoConectaSmall, IconTrash, IconWhiteboard } from "../base/icons/svg";
import Label from "../base/label/components/web/Label";
import { COLORS } from "../base/label/constants";
class ImageError extends Error {
    constructor(message) {
        super(message);
    }
}
export function ImageDialog({ initialImage, setImage, images, patientFileUploader, excalidrawApi }) {
    const [currentImage, setCurrentImage] = useState(initialImage);
    const [isLoading, setIsLoading] = useState(false);
    const [text, setText] = useState(patientFileUploader.getName());
    const dispatch = useDispatch();
    useEffect(() => {
        setImage(currentImage);
    }, [currentImage]);
    async function upload() {
        try {
            if (isLoading)
                throw new ImageError('Aun esta cargando una imagen');
            setIsLoading(true);
            if (!currentImage)
                throw new ImageError('No hay imagen seleccionada');
            const mimeTypeMatch = currentImage.dataURL.match(/^data:(image\/[a-zA-Z+]+);base64,/);
            if (!mimeTypeMatch) {
                throw new ImageError('La URL no es una imagen válida o no tiene formato data URL');
            }
            const mimeType = mimeTypeMatch[1];
            if (!mimeType.startsWith('image/')) {
                throw new ImageError('El archivo no es una imagen');
            }
            const extensionMap = {
                'image/jpeg': 'jpeg',
                'image/jpg': 'jpg',
                'image/png': 'png',
                'image/gif': 'gif',
                'image/webp': 'webp',
                'image/svg+xml': 'svg',
                'image/bmp': 'bmp',
                'image/tiff': 'tiff'
            };
            const extension = extensionMap[mimeType];
            if (!extension) {
                throw new ImageError(`Tipo de imagen no soportado: ${mimeType}`);
            }
            const fileName = `${text}.${extension}`;
            const blob = await base64ToBlob(currentImage.dataURL);
            try {
                await patientFileUploader.handle(blob, 7, fileName);
                dispatch(showSuccessNotification({
                    title: 'Exito',
                    description: 'La imagen ha sido añadida a la ficha clinica'
                }, NOTIFICATION_TIMEOUT_TYPE.LONG));
            }
            catch (error) {
                throw new ImageError('La imagen no ha podido ser añadida a la ficha clinica');
            }
            finally {
                setIsLoading(false);
            }
        }
        catch (error) {
            if (error instanceof ImageError) {
                dispatch(showErrorNotification({
                    title: 'Fallo',
                    description: error.message,
                    hideErrorSupportLink: true
                }, NOTIFICATION_TIMEOUT_TYPE.LONG));
            }
            else {
                dispatch(showErrorNotification({
                    title: 'Fallo',
                    description: 'Error desconocido',
                    hideErrorSupportLink: true
                }, NOTIFICATION_TIMEOUT_TYPE.LONG));
            }
        }
        finally {
            dispatch(hideDialog());
        }
    }
    async function base64ToBlob(base64String) {
        const response = await fetch(base64String);
        return await response.blob();
    }
    async function addToWhiteboard() {
        try {
            if (!currentImage)
                throw new ImageError('No hay imagen seleccionada');
            const img = new Image();
            const dimensions = await new Promise((resolve, reject) => {
                img.onload = () => {
                    resolve({
                        width: img.naturalWidth,
                        height: img.naturalHeight
                    });
                };
                img.onerror = () => {
                    reject(new ImageError('Error al cargar la imagen'));
                };
                img.src = currentImage.dataURL;
            });
            const state = excalidrawApi.getAppState();
            const min = -50;
            const max = 50;
            const random = Math.random() * (max - min) + min;
            const element = createImageElement({
                height: dimensions.height,
                width: dimensions.width,
                fileId: currentImage.id,
                x: (-state.scrollX + state.width / 2 - dimensions.width / 2) + random,
                y: (-state.scrollY + state.height / 2 - dimensions.height / 2) + random,
            });
            addElementsToExcalidraw(excalidrawApi, [element]);
        }
        catch (error) {
            if (error instanceof ImageError) {
                dispatch(showErrorNotification({
                    title: 'Fallo',
                    description: error.message,
                    hideErrorSupportLink: true
                }, NOTIFICATION_TIMEOUT_TYPE.LONG));
            }
            else {
                dispatch(showErrorNotification({
                    title: 'Fallo',
                    description: 'Error desconocido',
                    hideErrorSupportLink: true
                }, NOTIFICATION_TIMEOUT_TYPE.LONG));
            }
        }
        finally {
            dispatch(hideDialog());
        }
    }
    return (React.createElement(Dialog, { title: "Capturas", ok: { hidden: true }, onClose: () => setImage(undefined), cancel: { hidden: true } }, !currentImage ? (React.createElement(Container, null,
        React.createElement("div", { style: {
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
                gap: '16px',
                width: '100%',
                maxWidth: '600px',
                margin: '0 auto'
            } }, images.map((image, index) => (React.createElement("button", { key: index, style: {
                all: "unset",
                width: '100%',
                aspectRatio: '1',
                borderRadius: 8,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: 'hidden',
                border: '2px solid transparent',
                transition: 'all 0.2s ease',
                backgroundColor: 'rgba(255, 255, 255, 0.05)'
            }, onClick: () => setCurrentImage(image) },
            React.createElement("img", { style: {
                    width: "100%",
                    height: "100%",
                    objectFit: 'cover',
                }, src: image.dataURL, alt: `image - ${index + 1}` }))))))) : (React.createElement(Container, { style: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '24px',
        } },
        React.createElement("div", { style: {
                width: '100%',
                position: 'relative',
                maxWidth: '900px',
                borderRadius: '6px',
                overflow: 'hidden',
                boxSizing: 'border-box',
                boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
            } },
            React.createElement("div", { style: {
                    display: 'flex',
                    position: 'absolute',
                    top: 2,
                    right: 2
                } },
                React.createElement(Label, { iconColor: '#fff', onClick: () => setCurrentImage(undefined), icon: IconArrowBack }),
                React.createElement(Label, { color: COLORS.red, icon: IconTrash, iconColor: '#fff', onClick: () => { dispatch(openDialog(RemoveImageDialog, { image: currentImage })); } })),
            React.createElement("img", { src: currentImage.dataURL, alt: "image", style: {
                    width: '100%',
                    height: 'auto',
                    maxHeight: '65vh',
                    objectFit: 'contain',
                    borderRadius: '6px',
                    display: 'block',
                    margin: '0 auto',
                } })),
        React.createElement("div", { style: {
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                alignItems: 'center',
                width: '100%',
                maxWidth: '700px',
            } },
            React.createElement("input", { type: "text", placeholder: "Nombre del archivo...", 
                //@ts-ignore    
                onInput: (e) => setText(e.target.value), value: text, style: {
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: '6px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    background: 'transparent',
                    color: 'white',
                    fontSize: '14px',
                    outline: 'none',
                } }),
            React.createElement("div", { style: {
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                    width: '100%',
                } },
                React.createElement(Button, { fullWidth: true, label: isLoading ? 'Cargando...' : 'Subir a ficha clínica', icon: IconOdontoConectaSmall, onClick: () => { upload(); }, type: BUTTON_TYPES.ODONTO_CONECTA }),
                React.createElement(Button, { fullWidth: true, icon: IconWhiteboard, label: "Subir a la pizarra", onClick: () => { addToWhiteboard(); }, type: BUTTON_TYPES.PRIMARY })))))));
}
